import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { openLanding } from '../utils/landing';
import { PRO_PRICE_TEXT, PLUSZ_PRICE_TEXT, PLUSZ_PATH } from '../utils/pricing';
import { trackPaywall, requiredLevel } from '../utils/access';
import TrialStartButton from './TrialStart';

/* Plusz / PRO tab preview for users below the tab's level.
   The real tab renders with live data; only the first 3 rows/cards (found
   with `rowSelector`) stay sharp, everything below is blurred, and the whole
   preview is `inert` (no clicks, no keyboard focus). Above it, one card with
   what the tab gives, the price, "Plusszal nyílik" (Szkenner, RSI-áttekintés,
   Funding) or "PRO-val nyílik" (Stratégiák, ETF), and the trial button for
   whoever never had a trial.
   No dark patterns: the card can be closed, nothing blinks, no countdown. */

const PITCH = {
  scanner:    'Top 100 coin valódi RSI-vel és szűrőkkel – egy pillantással látod, mi túladott vagy túlvett.',
  signals:    'Valódi RSI négy idősíkon, 15 perctől napiig, leíró jelzésekkel.',
  funding:    'Minden fontos coin funding rátája és előzménye – látod, hol fizetnek túl a longok vagy a shortok.',
  strategies: 'Élő piaci feltételek a stratégiákhoz: funding, long/short arány és squeeze-helyzetek.',
  etf:        'Az ETF-piac összefoglalója egy helyen.',
};

export const ROW_SELECTOR = {
  scanner: '.gl-row',
  signals: '.sig-row',
  funding: '.fund-table tbody tr',
  strategies: '.strat-card',
  etf: '.etf-sum-card',
};

const VISIBLE_ROWS = 3;
const BLURRED_HEIGHT = 480; // px of blurred content shown below the cutoff
const viewed = new Set();   // one paywall_view per tab per page load

export default function ProPreview({ tab, user, access, onTrialStarted, children }) {
  const need = requiredLevel(tab); // 'plusz' | 'pro'
  const isPlusz = need === 'plusz';
  const contentRef = useRef(null);
  const [cutoff, setCutoff] = useState(360);
  const [cardOpen, setCardOpen] = useState(true);

  useEffect(() => {
    if (viewed.has(tab)) return;
    viewed.add(tab);
    trackPaywall('paywall_view', { tab }, user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Cutoff = bottom edge of the 3rd row (re-measured as data arrives).
  useLayoutEffect(() => {
    const box = contentRef.current;
    if (!box) return;
    const measure = () => {
      const rows = box.querySelectorAll(ROW_SELECTOR[tab]);
      if (!rows.length) return;
      const el = rows[Math.min(VISIBLE_ROWS, rows.length) - 1];
      const y = el.getBoundingClientRect().bottom - box.getBoundingClientRect().top;
      if (y > 0) setCutoff(Math.ceil(y) + 4);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    const mo = new MutationObserver(measure);
    mo.observe(box, { childList: true, subtree: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [tab]);

  const goPro = () => openLanding(isPlusz ? PLUSZ_PATH : '/pro', {
    user,
    event: isPlusz ? 'plusz_cta_click' : 'pro_cta_clicked',
    eventProps: isPlusz ? { hely: `terminal_${tab}` } : { source: 'tab', tab, tier: access?.tier || null },
  });
  const label = isPlusz ? 'Plusz-funkció' : 'PRO-funkció';
  const opens = isPlusz ? 'Plusszal nyílik →' : 'PRO-val nyílik →';
  const trial = <TrialStartButton hely={`terminal_${tab}`} access={access} user={user} onStarted={onTrialStarted} />;

  return (
    <div className="pw-wrap">
      {cardOpen ? (
        <div className={`pw-card${isPlusz ? ' pw-card--plusz' : ''}`} role="region" aria-label={label}>
          <button className="pw-close" aria-label="Ajánlat bezárása" onClick={() => setCardOpen(false)}>✕</button>
          <div className="pw-label">{label}</div>
          <p className="pw-pitch">{PITCH[tab]}</p>
          <div className="pw-row">
            <span className="pw-price">{isPlusz ? `Plusz: ${PLUSZ_PRICE_TEXT}` : `PRO: ${PRO_PRICE_TEXT}`}</span>
            <button className="pw-cta" onClick={goPro}>{opens}</button>
            {trial}
          </div>
        </div>
      ) : (
        <div className={`pw-mini${isPlusz ? ' pw-mini--plusz' : ''}`}>
          {label} — az első {VISIBLE_ROWS} sor látható.
          <button className="pw-mini-cta" onClick={goPro}>{opens}</button>
          {trial}
        </div>
      )}

      <div className="pw-content" style={{ maxHeight: cutoff + BLURRED_HEIGHT }}>
        <div ref={contentRef} inert aria-hidden="true">
          {children}
        </div>
        <div className="pw-blur" style={{ top: cutoff }} aria-hidden="true" />
      </div>
    </div>
  );
}
